/**
 * Role Service - API methods for role management
 */

import apiClient from './apiClient';
import { useAuthStore } from '../../stores/authStore';

/**
 * Get organizationId query params for super admin requests
 */
const getOrgParams = (): { organizationId?: string } => {
  const { user } = useAuthStore.getState();
  if (user?.isSuperAdmin && user?.organizationId) {
    return { organizationId: user.organizationId };
  }
  return {};
};

export type AccessLevel = 'READ' | 'FULL';

// Permission type (with access level)
export interface Permission {
  id: string;
  key: string;
  resource: string;
  displayName: string;
  description?: string;
  module?: string;
  accessLevel?: AccessLevel;
}

// Permission assignment for create/update
export interface PermissionAssignment {
  permissionId: string;
  accessLevel?: AccessLevel;
}

// Role diff against template
export interface RoleDiff {
  added: Array<{ key: string; accessLevel: AccessLevel }>;
  removed: string[];
  changed: Array<{ key: string; templateLevel: AccessLevel; customLevel: AccessLevel }>;
  totalChanges: number;
}

// Role types
export interface Role {
  id: string;
  name: string;
  description?: string;
  isSystem: boolean;
  /** Paid-role flag: system roles carry this flag; derived roles inherit it. Used for billing seat counts. */
  isPaid: boolean;
  organizationId: string | null;
  requiresSupervisor?: boolean;
  basedOnId?: string;
  basedOnName?: string;
  permissions?: Permission[];
  userCount?: number;
  groupCount?: number;
  derivedRoleCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Seat usage types for the billing panel (GET /roles/billing/seat-usage)
export interface SeatUsageByRole {
  roleId: string;
  roleName: string;
  seats: number;
}

export interface SeatUsageByOrganization {
  organizationId: string;
  organizationName: string;
  paidSeats: number;
  roles: SeatUsageByRole[];
}

export interface SeatUsage {
  totalPaidSeats: number;
  byOrganization: SeatUsageByOrganization[];
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Create role request
export interface CreateRoleRequest {
  name: string;
  description?: string;
  /** Required when creating a system role (super admin only). Inherited by derived roles. */
  isPaid?: boolean;
  requiresSupervisor?: boolean;
  permissionIds?: string[];
  permissions?: PermissionAssignment[];
}

// Update role request
export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  /** System-role only; derived roles inherit from parent. */
  isPaid?: boolean;
  requiresSupervisor?: boolean;
  permissionIds?: string[];
  permissions?: PermissionAssignment[];
}

// Create from template request
export interface CreateFromTemplateRequest {
  basedOnId: string;
  name: string;
  description?: string;
  permissions?: PermissionAssignment[];
}

// List roles params
export interface ListRolesParams {
  page?: number;
  limit?: number;
  search?: string;
  includeSystem?: boolean;
  organizationId?: string;
}

/**
 * Get paginated list of roles in organization
 */
export const getRoles = async (params: ListRolesParams = {}): Promise<PaginatedResponse<Role>> => {
  const response = await apiClient.get<{ success: boolean } & PaginatedResponse<Role>>(
    '/api/v1/roles',
    { params: { ...getOrgParams(), ...params } },
  );
  return {
    data: response.data.data,
    meta: response.data.meta,
  };
};

/**
 * Get role by ID with permissions
 */
export const getRoleById = async (id: string): Promise<Role> => {
  const response = await apiClient.get<{ success: boolean; data: Role }>(`/api/v1/roles/${id}`, {
    params: getOrgParams(),
  });
  return response.data.data;
};

/**
 * Create new role
 */
export const createRole = async (data: CreateRoleRequest): Promise<Role> => {
  const response = await apiClient.post<{ success: boolean; data: Role }>('/api/v1/roles', data, {
    params: getOrgParams(),
  });
  return response.data.data;
};

/**
 * Update role
 */
export const updateRole = async (id: string, data: UpdateRoleRequest): Promise<Role> => {
  const response = await apiClient.patch<{ success: boolean; data: Role }>(
    `/api/v1/roles/${id}`,
    data,
    { params: getOrgParams() },
  );
  return response.data.data;
};

/**
 * Create a new system role template (Super Admin only)
 * POST /roles/system — always org-context-free; does not use getOrgParams().
 * isPaid is required by the backend for system roles.
 */
export const createSystemRole = async (data: CreateRoleRequest): Promise<Role> => {
  const response = await apiClient.post<{ success: boolean; data: Role }>(
    '/api/v1/roles/system',
    data,
  );
  return response.data.data;
};

/**
 * Update system role (Super Admin)
 */
export const updateSystemRole = async (id: string, data: UpdateRoleRequest): Promise<Role> => {
  const response = await apiClient.patch<{ success: boolean; data: Role }>(
    `/api/v1/roles/system/${id}`,
    data,
  );
  return response.data.data;
};

/**
 * Delete role
 */
export const deleteRole = async (id: string): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ success: boolean; message: string }>(
    `/api/v1/roles/${id}`,
    { params: getOrgParams() },
  );
  return { message: response.data.message };
};

/**
 * Copy role with new name
 */
export const copyRole = async (id: string, newName: string): Promise<Role> => {
  const response = await apiClient.post<{ success: boolean; data: Role }>(
    `/api/v1/roles/${id}/copy`,
    { name: newName },
    { params: getOrgParams() },
  );
  return response.data.data;
};

/**
 * Get system role templates (Super Admin)
 */
export const getSystemRoles = async (): Promise<Role[]> => {
  const response = await apiClient.get<{ success: boolean; data: Role[] }>(
    '/api/v1/roles/system',
  );
  return response.data.data;
};

/**
 * Create custom role from system role template
 */
export const createFromTemplate = async (data: CreateFromTemplateRequest): Promise<Role> => {
  const response = await apiClient.post<{ success: boolean; data: Role }>(
    '/api/v1/roles/from-template',
    data,
    { params: getOrgParams() },
  );
  return response.data.data;
};

/**
 * Get diff between role and its base template
 */
export const getRoleDiff = async (id: string): Promise<RoleDiff> => {
  const response = await apiClient.get<{ success: boolean; data: RoleDiff }>(
    `/api/v1/roles/${id}/diff`,
    { params: getOrgParams() },
  );
  return response.data.data;
};

/**
 * Get paid-seat usage summary (Super Admin only)
 * GET /roles/billing/seat-usage
 */
export const getSeatUsage = async (): Promise<SeatUsage> => {
  const response = await apiClient.get<{ success: boolean; data: SeatUsage }>(
    '/api/v1/roles/billing/seat-usage',
  );
  return response.data.data;
};

/**
 * Update a role's workflow access + Team/Clients data scope (org-admin editor).
 * PATCH /roles/:id/access
 */
export interface UpdateRoleAccessRequest {
  workflows?: Record<string, 'none' | 'execute' | 'manage'>;
  scopes?: {
    team?: 'none' | 'own' | 'all';
    clients?: 'none' | 'own' | 'all';
    locations?: 'none' | 'own' | 'all';
  };
}

export const updateRoleAccess = async (
  id: string,
  data: UpdateRoleAccessRequest,
): Promise<Role> => {
  const response = await apiClient.patch<{ success: boolean; data: Role }>(
    `/api/v1/roles/${id}/access`,
    data,
    { params: getOrgParams() },
  );
  return response.data.data;
};

const roleService = {
  getRoles,
  getRoleById,
  createRole,
  createSystemRole,
  updateRole,
  updateSystemRole,
  updateRoleAccess,
  deleteRole,
  copyRole,
  getSystemRoles,
  createFromTemplate,
  getRoleDiff,
  getSeatUsage,
};

export default roleService;
